
import React, { useState, useEffect, useMemo } from 'react';
import { User, UserRole, UserStatus } from '@/types';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon, TrashIcon } from '@/components/Icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImageUploader } from '@/components/ImageUploader';
import { PasswordInput } from '@/components/PasswordInput';
import { Shield, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const userRoles: UserRole[] = ['Administrador', 'Editor', 'Lector'];
const userStatuses: UserStatus[] = ['Activo', 'Inactivo'];

const getStatusBadgeVariant = (status: UserStatus) => {
  return status === 'Activo' ? 'success' : 'secondary';
};

const getInitials = (name: string) => {
  if (!name) return '';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

export default function Usuarios() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<Partial<User> & { password?: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; onConfirm?: () => void; }>({ isOpen: false });
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(false);
  const { fetchWithAuth } = useUser(); // Use fetchWithAuth from context

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      setPermissionError(false);
      try {
        const response = await fetchWithAuth('/users');
        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 403) {
            // Permission error - user is not admin
            setPermissionError(true);
            return;
          }
          throw new Error(errorData.message || 'Error al cargar los usuarios');
        }
        const data = await response.json();
        setUsers(data);
      } catch (error: any) {
        console.error('Error loading users:', error);
        toast.error('Error al cargar los usuarios', {
          description: error.message || 'Verifica que tienes permisos de administrador'
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadUsers();
  }, [fetchWithAuth]);

  const filteredUsers = useMemo(() => {
    const lowercasedSearch = search.toLowerCase();
    return users.filter(user => 
      (user.name && user.name.toLowerCase().includes(lowercasedSearch)) ||
      (user.email && user.email.toLowerCase().includes(lowercasedSearch))
    );
  }, [users, search]);

  const handleAdd = () => {
    setCurrentUser({ name: '', email: '', role: 'Lector', status: 'Activo', avatar: '', phone: '', password: '' });
    setDialogOpen(true);
  };

  const handleEdit = (user: User) => {
    setCurrentUser(user);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (users.length <= 1) {
        toast.warning('Acción no permitida', { description: 'No se puede eliminar el único usuario.' });
        return;
    }
    setDeleteDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await fetchWithAuth(`/users/${id}`, { method: 'DELETE' });
          setUsers(prev => prev.filter(u => u.id !== id));
          toast.success('Usuario eliminado con éxito.');
        } catch (error) {
            console.error(error);
            toast.error('Error al eliminar el usuario.');
        }
        setDeleteDialog({ isOpen: false });
      }
    });
  };

  const handleSave = async () => {
    if (!currentUser || !currentUser.name || !currentUser.email) {
      toast.error('Error', { description: 'El nombre y el email son obligatorios.' });
      return;
    }

    const isCreating = !currentUser.id;

    if (isCreating && !currentUser.password) {
        toast.error('Error', { description: 'La contraseña es obligatoria al crear un usuario.' });
        return;
    }

    try {
        let savedUser: User;
        if (isCreating) {
            // Create user
            const response = await fetchWithAuth('/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(currentUser),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al crear el usuario');
            }
            savedUser = await response.json();
            setUsers(prev => [savedUser, ...prev]);
            toast.success('Usuario creado con éxito.');
        } else {
            // Update user
            const response = await fetchWithAuth(`/users/${currentUser.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(currentUser),
            });
            if (!response.ok) {
                 const errorData = await response.json();
                throw new Error(errorData.message || 'Error al actualizar el usuario');
            }
            savedUser = await response.json();
            setUsers(prev => prev.map(u => (u.id === savedUser.id ? savedUser : u)));
            toast.success('Usuario actualizado con éxito.');
        }

        setDialogOpen(false);
        setCurrentUser(null);

    } catch (error: any) {
        console.error(error);
        toast.error('Error al guardar el usuario', { description: error.message });
    }
  };

  // If permission error, show special message
  if (permissionError) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Usuarios</h1>
          <p className="text-muted-foreground mt-1">Gestiona los usuarios y permisos de la plataforma.</p>
        </div>

        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-3 bg-yellow-500/10 rounded-full">
                <AlertTriangle className="h-10 w-10 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Acceso Restringido</h3>
                <p className="text-muted-foreground mb-4">
                  No tienes permisos de administrador para acceder a la gestión de usuarios.
                </p>
              </div>
              <Alert className="max-w-md">
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  Para acceder a esta sección, necesitas que tu usuario tenga el rol de <strong>Administrador</strong>.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground max-w-md">
                Para obtener permisos de administrador, pide a un administrador
                existente que te asigne el rol, o ejecuta el script de arranque
                <code className="mx-1">scripts/setup-first-admin.cjs</code>.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Usuarios</h1>
          <p className="text-muted-foreground mt-1">Gestiona los usuarios y permisos de la plataforma.</p>
        </div>
        <Button onClick={handleAdd}><PlusIcon className="mr-2 h-4 w-4"/> Nuevo Usuario</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listado de Usuarios</CardTitle>
          <CardDescription>Crea, edita y gestiona los roles de los usuarios.</CardDescription>
          <div className="mt-4">
            <Input 
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha de Alta</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={5} className="h-24 text-center">Cargando usuarios...</TableCell></TableRow>
                ) : filteredUsers.length > 0 ? filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell><Badge variant={getStatusBadgeVariant(user.status)}>{user.status}</Badge></TableCell>
                    <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(user)}><PencilIcon className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)}><TrashIcon className="w-4 h-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">No hay usuarios.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* --- User Form Dialog --- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{currentUser?.id ? 'Editar Usuario' : 'Crear Usuario'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
             <ImageUploader 
                value={currentUser?.avatar || ''} 
                onValueChange={value => setCurrentUser(prev => ({ ...prev, avatar: value }))}
            />
            <Input 
              placeholder="Nombre completo"
              value={currentUser?.name || ''}
              onChange={e => setCurrentUser({ ...currentUser, name: e.target.value })}
            />
            <Input
              type="email"
              placeholder="Email"
              value={currentUser?.email || ''}
              onChange={e => setCurrentUser({ ...currentUser, email: e.target.value })}
              disabled={!!currentUser?.id} // Disable email editing when updating existing user
            />
             <PasswordInput 
              placeholder={currentUser?.id ? "Dejar en blanco para no cambiar" : "Contraseña"}
              value={currentUser?.password || ''}
              onChange={e => setCurrentUser({ ...currentUser, password: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
              <Select
                value={currentUser?.role || 'Lector'}
                onValueChange={(value: UserRole) => setCurrentUser({ ...currentUser, role: value })}
              >
                <SelectTrigger><SelectValue placeholder="Rol" /></SelectTrigger>
                <SelectContent>
                  {userRoles.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={currentUser?.status || 'Activo'}
                onValueChange={(value: UserStatus) => setCurrentUser({ ...currentUser, status: value })}
              >
                <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  {userStatuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
             <Input 
              type="tel"
              placeholder="Teléfono (opcional)"
              value={currentUser?.phone || ''}
              onChange={e => setCurrentUser({ ...currentUser, phone: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Guardar Usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Delete Confirmation Dialog --- */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(isOpen) => !isOpen && setDeleteDialog({ isOpen: false }) }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Se eliminará el usuario permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteDialog.onConfirm}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
